import _ from 'lodash'
import moment from 'moment'
import $ from 'jquery'
import fileSize from 'filesize'
import Vue from 'vue'
import { Store } from 'vuex'
import { RawLocation, Route } from 'vue-router'
import Component from 'vue-class-component'
import RootState from '../../store/state'
import {
  ARTICLE_ID,
  ComponentStore,
  EmbedData,
  GerberaContent,
  ScriptContent,
  TextData,
  UrlData
} from '../../store/editor/components/state'
import * as CommonHelpers from '../../store/common/helpers'
import * as EditorCoreHelpers from '../../store/editor/core/helpers'
import * as ManagerHelpers from '../../store/manager/helpers'
import { Actions, Getters, Mutations } from '../../store/editor/components/helpers'
import DropMarkerVue from './DropMarker.vue'
import ArticleVue from './Article.vue'
import { CONTENTS_CONTAINER_SELECTOR } from '../constants/ContainerSelector'
import { MAX_TITLE_LENGTH } from '../constants/TitleLength'
import PreviewVue from '../preview/Preview.vue'
import HtmlGeneratorVue from '../preview/HtmlGenerator.vue'
import PublishConfig from '../dialog/PublishConfig.vue'
import { NO_EXPIRE, PUBLISH_CONFIG_DRAFT, PublishConfigData } from '../dialog/PublishConfigInterface'
import EmbedEditor from '../dialog/EmbedEditor.vue'
import { Category, default as ContentEndpoint } from '../../api/gerbera/contents'
import HelpPageLinkVue from '../form/HelpPageLink.vue'
import { isNotFound, isRequestSizeOver, isSizeLimitExceeded, isUnauthorized } from '../../misc/get-status-code'
import { ErrorStatus } from '../../store/common/state'
import retryPromise, { createRetryHandler, RetryHandler } from '../../misc/retry-promise'
import MenuVue from './Menu.vue'
import UrlEditor from '../dialog/UrlEditor.vue'
import { OembedLink } from '../dialog/UrlEditorInterface'
import { Watch } from 'vue-property-decorator'
import loadScript from '../../misc/load-script'
import GetSiteUrl from '../dialog/GetSiteUrl.vue'
import { SiteUrlModel } from '../../store/manager/state'
import { ArticleDetailUrlFactory } from '../../misc/generate-url'
import { INITIAL_COLOR } from '../constants/ColorPicker'

moment.locale('ja')

type SaveButtonType = 'publishOrUpdate' | 'draft'

// API と通信して認証を維持する間隔（20分）
const KEEP_ALIVE_DURATION = 20 * 1000 * 60
// バックアップを保存する最短の間隔（5秒）
const BACKUP_MIN_DURATION = 1000 * 5

// vue-router のイベントフックを登録する
Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteLeave',
  'beforeRouteUpdate'
])

@Component({
  components: {
    'article-vue': ArticleVue,
    'drop-marker': DropMarkerVue,
    'preview-vue': PreviewVue,
    'html-generator': HtmlGeneratorVue,
    'publish-config': PublishConfig,
    'embed-editor': EmbedEditor,
    'url-editor': UrlEditor,
    'help-link': HelpPageLinkVue,
    'editor-menu': MenuVue,
    'get-site-url': GetSiteUrl
  },
  props: {
    skipLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    restoreFromBackup: {
      type: Boolean,
      required: false,
      default: false
    }
  }
})
export default class EditorVue extends Vue {
  $store: Store<RootState>
  $route: Route
  readonly skipLoading: boolean
  readonly restoreFromBackup: boolean
  isLoading: boolean = false
  isLoaded: boolean = false
  isInitialized: boolean = false
  isNewArticle: boolean = false
  hasLoadingError: boolean = false
  isDroppingImage: boolean = false
  isPrepareingToSave: boolean = false
  isSaving: boolean = false
  isExportContentVisible: boolean = false
  isPreparingToAddEmbed: boolean = false
  isPreparingToAddUrl: boolean = false
  isPreparingToSiteUrl: boolean = false
  isSaveDropdownVisible: boolean = false
  shouldBackupDataDeleted: boolean = false
  saveButtonType: SaveButtonType = 'publishOrUpdate'
  retryHandler: RetryHandler = createRetryHandler()
  keepAlive: any = -1
  saveBackup: () => void = () => {
  }
  beforeUnload: (event: BeforeUnloadEvent) => any = () => {
  }
  unload: (event: Event) => any = () => {
  }

  get articleId(): string {
    return ARTICLE_ID
  }

  get paramKajikiId(): number {
    return parseInt(this.$route.params['kajiki_id'], 10)
  }

  get paramContentId(): number | undefined {
    const contentId = this.$route.params['content_id']
    return contentId ? parseInt(contentId, 10) : undefined
  }

  get gerberaContent(): GerberaContent {
    return Getters.gerberaContent(this.$store)
  }

  get category(): Category {
    return this.gerberaContent.category || { categoryId: null }
  }

  get categoryId(): number | null {
    return this.category.categoryId || null
  }

  get saveDialogContent(): PublishConfigData {
    return Object.assign({}, <PublishConfigData>{
      status: 'published'
    }, this.gerberaContent)
  }

  get components(): ComponentStore {
    return this.$store.state.editor.components.data
  }

  get currentTitle(): string | undefined {
    return this.gerberaContent.title
  }

  get noTitle(): boolean {
    return !this.currentTitle
  }

  get isTitleLengthOver(): boolean {
    return !!this.currentTitle && (this.currentTitle.length > MAX_TITLE_LENGTH)
  }

  get alertMessageOnSave(): string {
    if (this.noTitle) {
      return '記事のタイトルを入力してください。'
    }
    if (this.isTitleLengthOver) {
      return `記事のタイトルは${MAX_TITLE_LENGTH}文字以下で入力してください。`
    }
    return ''
  }

  get isContentSaved(): boolean {
    return Getters.isContentSaved(this.$store)
  }

  get isPreview(): boolean {
    return this.$route.hash === '#preview'
  }

  set isPreview(isPreview: boolean) {
    this.$router.push({
      name: this.$route.name,
      params: this.$route.params,
      hash: (isPreview) ? '#preview' : ''
    })
  }

  get isDraft(): boolean {
    return !this.paramContentId || this.gerberaContent.status === 'pending'
  }

  get backTo(): object {
    return {
      name: 'contents-manage',
      params: {
        kajiki_id: this.paramKajikiId.toString()
      }
    }
  }

  get errorStatus(): ErrorStatus {
    return CommonHelpers.Getters.errorStatus(this.$store)
  }

  get canUndo(): boolean {
    return Getters.canUndo(this.$store)
  }

  get canRedo(): boolean {
    return Getters.canRedo(this.$store)
  }

  get embedScripts(): ScriptContent[] {
    return Getters.embedScripts(this.$store)
  }

  get siteUrlEvent(): SiteUrlModel {
    const kajikiList = ManagerHelpers.Getters.kajikiList(this.$store)
    const kajiki = kajikiList.find(kajiki => kajiki.kajikiId === this.paramKajikiId)
    const data = {
      categoryId: this.categoryId,
      categoryName: this.gerberaContent.category.categoryName,
      contentId: this.gerberaContent.contentId,
      status: this.gerberaContent.status,
      protocol: kajiki.protocol,
      domain: kajiki.domain,
      isPublished: kajiki.isPublished,
      contentDetailPrefixPath: kajiki.contentDetailPrefixPath
    }

    return new ArticleDetailUrlFactory('get-site-url', data).create()
  }

  created(): void {
    this.keepAlive = setInterval(() => {
      // 突然ログアウトされる現象を防ぐため、 API と通信して認証を維持する
      new ContentEndpoint().pingRequest()
    }, KEEP_ALIVE_DURATION)

    this.saveBackup = _.throttle(() => {
      Actions.saveToBackup(this.$store)
      this.shouldBackupDataDeleted = false
    }, BACKUP_MIN_DURATION)
  }

  mounted(): void {
    this.loadContent()
    this.refreshScriptPool()
    this.bindBeforeUnload()
  }

  destroyed(): void {
    clearInterval(this.keepAlive)
    this.unbindBeforeUnload()
  }

  beforeRouteLeave(to: Route, from: Route, next: (to?: RawLocation | false | void) => void): void {
    if (from.meta && to.meta && from.meta.type === to.meta.type) {
      // 遷移元と遷移先が同じコンテンツ編集画面の場合、 mounted() が実行されず bindBeforeUnload() が呼ばれない。
      // そのため、 ここで unbindBeforeUnload() を呼ばないようにする。
      next()
      return
    }
    if (this.isContentSaved) {
      this.unbindBeforeUnload()
      next()
      return
    }

    if (this.confirmBeforeRouteLeave()) {
      // 離脱確認してOKだった場合、バックアップを削除する
      Actions.removeBackup(this.$store)
      this.unbindBeforeUnload()
      next()
      return
    }
    next(false)
  }

  confirmBeforeRouteLeave(): boolean {
    return confirm('記事を保存せずに終了しますか？')
  }

  bindBeforeUnload(): void {
    this.beforeUnload = (event) => {
      return this.confirmBeforeUnload(event)
    }
    window.addEventListener('beforeunload', this.beforeUnload)

    this.unload = () => {
      if (this.shouldBackupDataDeleted) {
        // 離脱確認してOKだった場合、バックアップを削除する
        Actions.removeBackup(this.$store)
      }
    }
    window.addEventListener('unload', this.unload)
  }

  unbindBeforeUnload(): void {
    window.removeEventListener('beforeunload', this.beforeUnload)
    window.removeEventListener('unload', this.unload)
  }

  confirmBeforeUnload(event: BeforeUnloadEvent): string | undefined {
    if (this.isContentSaved || !!this.errorStatus) {
      this.shouldBackupDataDeleted = false
      this.unbindBeforeUnload()
      return undefined
    }
    this.shouldBackupDataDeleted = true
    const confirmMessage = '記事を保存せずに終了しますか？'
    event.returnValue = confirmMessage
    return confirmMessage
  }

  async loadContent(): Promise<void> {
    if (this.skipLoading) {
      // 既に store が初期化されているので何もしない。
      this.isLoading = false
      this.isLoaded = true
      this.isInitialized = true
      return
    }

    this.initializeState()
    EditorCoreHelpers.Mutations.initEditor(this.$store)
    Mutations.initializeContent(this.$store, this.paramKajikiId)
    this.isInitialized = true

    if (this.restoreFromBackup) {
      await this.loadFromBackup()
      this.isNewArticle = !this.paramContentId
    } else if (this.paramContentId) {
      await this.loadExistingContent(this.paramContentId)
    } else {
      await this.initializeNewContent()
      this.isNewArticle = true
    }
    this.checkStorageSize()

    // 保存時にカテゴリが必須なので、事前に読み込んでおく。仮に通信が失敗しても、ここでは問題ない
    this.loadCategories()
  }

  loadCategories(): Promise<void> {
    if (this.$store.state.manager.categoryData.length > 0) {
      // 既に読み込み済み
      return Promise.resolve()
    }
    return ManagerHelpers.Actions.loadCategories(this.$store, this.paramKajikiId)
  }

  initializeState(): void {
    this.isLoading = false
    this.isLoaded = false
    this.isInitialized = false
    this.isNewArticle = false
    this.hasLoadingError = false
    this.isDroppingImage = false
    this.isPrepareingToSave = false
    this.isSaving = false
    this.isExportContentVisible = false
    this.isPreparingToAddEmbed = false
    this.isSaveDropdownVisible = false
    this.shouldBackupDataDeleted = false
    this.saveButtonType = 'publishOrUpdate'
  }

  initHeaderSectionBackground(): void {
  // ヘッダーセクションの背景色の初期値を設定
    const data = Getters.headerSectionContent(this.$store)
    if (!data.background) {
      Mutations.setHeaderSectionContent(this.$store, {
        background: {
          color: INITIAL_COLOR,
          image: undefined
        }
      })
    }
  }

  // セクションの背景色の初期値を設定
  initSectionBackground(): void {
    const article = Getters.article(this.$store)
    for (let i = 0; i < article.childIds.length; i++) {
      const data = Getters.componentFinder(this.$store)(article.childIds[i])
      const backgroundColor = data.background.color

      if (!backgroundColor) {
        Actions.update(this.$store, Object.assign({}, data, {
          background: {
            color: INITIAL_COLOR
          }
        }))
      }
    }
  }

  async loadExistingContent(contentId: number): Promise<void> {
    // 既存のコンテンツを読み込むときの処理
    this.isLoading = true
    try {
      await Actions.loadContent(this.$store, {
        contentId,
        siteId: this.paramKajikiId
      })
      this.initHeaderSectionBackground()
      this.initSectionBackground()
      this.saveButtonType = this.suggestSaveButtonType()
      this.isLoading = false
      this.isLoaded = true
    } catch (reason) {
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        this.isLoading = false
        return
      }
      if (isNotFound(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'not-found')
        this.isLoading = false
        return
      }
      this.hasLoadingError = true
      this.isLoading = false
    }
  }

  suggestSaveButtonType(): SaveButtonType {
    if (!this.paramContentId) {
      return 'publishOrUpdate'
    }
    return (this.gerberaContent.status === 'pending') ? 'draft' : 'publishOrUpdate'
  }

  async initializeNewContent(): Promise<void> {
    // コンテンツを新規作成するときの処理
    //  store を初期化する
    this.fillInitialValues()
    this.initHeaderSectionBackground()
    this.isLoading = false
    this.isLoaded = true
    await this.removeQueryStringFromLocation()
  }

  fillInitialValues(): void {
    // クエリストリングからタイトル等の初期値を受け取る
    const query = this.$route.query

    const initialValues: GerberaContent = {}
    if (query.title) {
      initialValues.title = query.title
    }
    if (query.category_id) {
      initialValues.category = {
        categoryId: parseInt(query.category_id, 10) || undefined
      }
    }
    if (_.size(initialValues) > 0) {
      Mutations.setGerberaContent(this.$store, Object.assign({}, Getters.gerberaContent(this.$store), initialValues))
      Mutations.markContentSaved(this.$store)
    }
  }

  async removeQueryStringFromLocation(): Promise<void> {
    if (_.size(this.$route.query) > 0) {
      // クエリストリングを空にして、自分自身のURLを再読み込みする
      // 次回は vue-router の設定により this.skipLoading が true になるので、初期化がスキップされ、タイトル等の初期値は保持される
      return new Promise<void>((resolve, reject) => {
        this.$router.replace({
          name: 'editor-new-without-loading',
          params: this.$route.params
        }, resolve, () => {
          reject(new Error('URLの書き換えに失敗。'))
        })
      })
    }
  }

  async loadFromBackup(): Promise<void> {
    try {
      this.isLoading = true
      await Actions.loadFromBackup(this.$store, this.paramKajikiId)
      await this.forceUpdateUrl()
      await this.$nextTick()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-editor--restore-backup',
        level: 'warning',
        message: '前回編集され、保存されていない記事データを復旧しました。改めて保存を行ってください。'
      })
      this.isLoading = false
      this.isLoaded = true
    } catch (reason) {
      this.hasLoadingError = true
      this.isLoading = false
    }
  }

  // FIXME テスト用のバックアップ復旧処理。後で削除すること
  async testLoadFromBackup(): Promise<void> {
    try {
      this.isLoading = true
      await Actions.loadFromBackup(this.$store, this.paramKajikiId)
      this.isLoading = false
      this.isLoaded = true
    } catch (reason) {
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-editor--save',
        level: 'warning',
        message: reason.message
      })
      this.isLoading = false
    }
  }

  async checkStorageSize(): Promise<void> {
    await this.$nextTick()
    // 画面を開いたとき、残り容量がわずかであれば警告を出す
    try {
      const storageSummary = await new ContentEndpoint().loadStorageData(this.paramKajikiId)
      const use = storageSummary.storage.use
      const total = storageSummary.storage.total
      const useRate = use / total
      if (useRate >= .9) {
        const rest = fileSize(Math.max(total - use, 0))
        CommonHelpers.Actions.addNotification(this.$store, {
          type: 'content-editor--save',
          level: 'warning',
          message: `記事データの空き容量が残りわずかです。(残り:${rest})\n容量プランの変更には、いい生活 担当営業までご連絡ください。`
        })
      }
    } catch (reason) {
      // 残り容量に関する情報は重要ではない。エラーは無視する
    }
  }

  prepareFocusChange(): void {
    EditorCoreHelpers.Mutations.prepareFocusChange(this.$store)
  }

  commitFocusChange(): void {
    EditorCoreHelpers.Mutations.commitFocusChange(this.$store)
  }

  showDropPoint(event: DragEvent): void {
    if (this.isLoaded && this.containsImage(event)) {
      this.isDroppingImage = true
    }
  }

  containsImage(event: DragEvent): boolean {
    // dataTransfer.items をサポートしていないブラウザは、ドラッグ＆ドロップによるアップロードを受け付けない。
    // 画像のドラッグのみ受け付ける。
    return event.dataTransfer.items
      && _.some(event.dataTransfer.items, (item: DataTransferItem) => {
        return this.isContentTypeAcceptable(item.type)
      })
  }

  isContentTypeAcceptable(contentType: string): boolean {
    // webp や svg も対応した方が良い気がする…
    return ['image/jpeg', 'image/png', 'image/gif'].indexOf(contentType) >= 0
  }

  hideDropPoint(event: DragEvent): void {
    this.isDroppingImage = false
  }

  readyAccept(event: DragEvent): void {
    event.preventDefault()
    event.dataTransfer.effectAllowed = 'copy'
    event.dataTransfer.dropEffect = 'copy'
  }

  handleDrop(event: DragEvent): void {
    if (!this.isLoaded) {
      return
    }
    // 画像だけを取り出す
    const contentTypes: string[] = _.map(event.dataTransfer.items, item => item.type)
    const files: File[] = _.filter(event.dataTransfer.files, (file, index) => {
      return this.isContentTypeAcceptable(contentTypes[index])
    })
    this.doAddImage(files)
    event.preventDefault()
    event.stopPropagation()
    this.isDroppingImage = false
  }

  addText(): void {
    const id = Getters.idGenerator(this.$store)()
    const text = new TextData(id, '')
    Actions.addToArticle(this.$store, text)
    this.scrollBottom()
  }

  addEmbed(content: string): void {
    this.isPreparingToAddEmbed = false
    const id = Getters.idGenerator(this.$store)()
    const embed = new EmbedData(id, content)
    Actions.addToArticle(this.$store, embed)
    this.scrollBottom()
  }

  addUrl(link: OembedLink): void {
    this.isPreparingToAddUrl = false
    const id = Getters.idGenerator(this.$store)()
    const url = new UrlData(id, link)
    Actions.addToArticle(this.$store, url)
    this.scrollBottom()
  }

  async addImage(files: FileList | File[]): Promise<void> {
    await this.doAddImage(files)
  }

  async doAddImage(files: FileList | File[]): Promise<void> {
    try {
      const sources = await Actions.uploadImages(this.$store, files)
      const dataList = await Promise.all(sources.map(s => Actions.createImageData(this.$store, s)))
      await Actions.addToArticle(this.$store, dataList)
      this.scrollBottom()
    } catch (reason) {
      CommonHelpers.Actions.addNotification(this.$store, {
        level: 'warning',
        message: reason
      })
    }
  }

  showPreview(): void {
    Actions.requestSaveState(this.$store).then(() => {
      this.isPreview = true
      return this.$nextTick()
    })
  }

  hidePreview(): void {
    this.$router.back()
    this.refreshScriptPool()
  }

  showSaveDialog(): void {
    if (this.validateBeforeSave()) {
      this.loadCategories()
        .then(() => {
          this.isPrepareingToSave = true
        })
        .catch((reason) => {
          // カテゴリが読み込めないので、エラーとする
          console.error(reason)
          CommonHelpers.Actions.addNotification(this.$store, {
            type: 'content-editor--save',
            level: 'warning',
            message: '通信に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
          })
        })
    }
  }

  hideSaveDialog(): void {
    this.isPrepareingToSave = false
  }

  validateBeforeSave(): boolean {
    if (this.alertMessageOnSave) {
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-editor--save',
        level: 'warning',
        message: this.alertMessageOnSave
      })
      this.scrollTop()
      return false
    }
    return true
  }

  showEmbedDialog(): void {
    this.isPreparingToAddEmbed = true
  }

  hideEmbedDialog(): void {
    this.isPreparingToAddEmbed = false
  }

  showUrlDialog(): void {
    this.isPreparingToAddUrl = true
  }

  hideUrlDialog(): void {
    this.isPreparingToAddUrl = false
  }

  showSiteUrlDialog(): void {
    this.isPreparingToSiteUrl = true
  }

  hideSiteUrlDialog(): void {
    this.isPreparingToSiteUrl = false
  }

  trackInputEmbedText(): void {
  }

  trackInputUrlText(): void {
  }

  toggleSaveDropdown(): void {
    if (!this.validateBeforeSave()) {
      this.hideSaveDropdown()
      return
    }
    this.isSaveDropdownVisible = !this.isSaveDropdownVisible
  }

  hideSaveDropdown(): void {
    this.isSaveDropdownVisible = false
  }

  scrollTop(): void {
    this.$nextTick(() => {
      $(CONTENTS_CONTAINER_SELECTOR).animate({
        scrollTop: 0
      })
    })
  }

  scrollBottom(): void {
    this.$nextTick(() => {
      const $contents = $(CONTENTS_CONTAINER_SELECTOR)
      const contents = $(CONTENTS_CONTAINER_SELECTOR).get(0)
      const bottom = contents.scrollHeight - contents.clientHeight
      $contents.animate({
        scrollTop: bottom
      }, {
        duration: 200,
        queue: false
      })
    })
  }

  save(publishConfig: PublishConfigData): void {
    if (this.isSaving || !this.validateBeforeSave()) {
      return
    }
    const originalContent = {
      publishedDate: this.gerberaContent.publishedDate,
      expiresDate: this.gerberaContent.expiresDate
    }
    this.isPrepareingToSave = false
    this.isSaving = true
    this.isNewArticle = false
    Actions.requestSaveState(this.$store).then(() => {
      return Actions.updateGerberaContent(this.$store, Object.assign({}, this.gerberaContent, <GerberaContent>{
        category: publishConfig.category,
        publishedDate: publishConfig.publishedDate,
        expiresDate: publishConfig.expiresDate
      }))
    }).then(() => {
      // 日付が出力用HTMLに反映されるまで待つ
      this.isExportContentVisible = true
      return this.$nextTick()
    }).then(() => {
      return retryPromise<void>((retry, count) => {
        return Actions.saveContent(this.$store, { html: this.getHtmlContent() })
          .catch((reason) => {
            return this.retryHandler(reason, retry, count)
          })
      })
    }).then(() => {
      return this.updateUrl()
    }).then(() => {
      this.isSaving = false
      this.isExportContentVisible = false
      this.saveButtonType = this.suggestSaveButtonType()
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-editor--save',
        message: '記事を保存しました。'
      })
    }).catch((reason) => {
      this.isSaving = false
      this.isExportContentVisible = false
      Mutations.setGerberaContent(this.$store, Object.assign({}, this.gerberaContent, <GerberaContent>{
        // 日付が保存されなかった。公開状態がサーバ側と一致しない可能性があるので、 store の中身を元の値に戻す
        publishedDate: originalContent.publishedDate,
        expiresDate: originalContent.expiresDate
      }))
      if (isUnauthorized(reason)) {
        CommonHelpers.Mutations.setErrorStatus(this.$store, 'session-timeout')
        return
      }
      if (isSizeLimitExceeded(reason)) {
        CommonHelpers.Actions.addNotification(this.$store, {
          type: 'content-editor--save',
          level: 'warning',
          message: 'ご契約中のプランで利用可能なデータ容量を超えました。\nこれ以上記事を保存できません。\n容量プランの変更には、いい生活 担当営業までご連絡ください。'
        })
        return
      }
      if (isRequestSizeOver(reason)) {
        CommonHelpers.Actions.addNotification(this.$store, {
          type: 'content-editor--save',
          level: 'warning',
          message: '記事のデータ量が上限（10MB）を超えました。\n画像のファイルサイズや枚数、テキストの量を減らしてください。'
        })
        return
      }
      console.error(reason)
      CommonHelpers.Actions.addNotification(this.$store, {
        type: 'content-editor--save',
        level: 'warning',
        message: '保存に失敗しました。通信環境をご確認のうえ、もう一度お試しください。'
      })
    })
  }

  trackSaveButton(): void {
    const label = this.isDraft ? '公開' : '更新'
    this.publishOrUpdate()
  }

  trackSaveMenu(): void {
    const label = this.isDraft ? '公開' : '更新'
    this.publishOrUpdate()
  }

  publishOrUpdate(): void {
    if (this.isDraft) {
      this.publish()
      return
    }
    this.update()
  }

  publish(): void {
    this.saveButtonType = 'publishOrUpdate'
    this.save({
      category: {
        categoryId: this.categoryId || null
      },
      publishedDate: moment().format(),
      expiresDate: NO_EXPIRE.format(),
      status: 'published'
    })
  }

  update(): void {
    this.saveButtonType = 'publishOrUpdate'
    this.save({
      category: {
        categoryId: this.categoryId || null
      },
      publishedDate: this.gerberaContent.publishedDate || moment().format(),
      expiresDate: this.gerberaContent.expiresDate || NO_EXPIRE.format(),
      status: this.gerberaContent.status || 'published'
    })
  }

  saveDraft(): void {
    if (!this.isDraft && !confirm('下書き保存すると、記事が非公開になります。\n保存しますか？')) {
      return
    }
    this.saveButtonType = 'draft'
    this.save(Object.assign({}, PUBLISH_CONFIG_DRAFT, <PublishConfigData>{
      category: {
        categoryId: this.categoryId
      }
    }))
  }

  /**
   * 記事のIDをURLに反映する
   * @param {boolean} force - 強制的にURLを更新する場合、 true
   */
  async updateUrl(force: boolean = false): Promise<void> {
    if (!force
      && this.gerberaContent.siteId === this.paramKajikiId
      && this.gerberaContent.contentId === this.paramContentId) {
      // 記事の内容と URL が一致している場合、何もしない
      return
    }

    // 記事から取得した ID を URL に反映する
    await this.$nextTick()
    await new Promise<void>(async (resolve, reject) => {
      let location: RawLocation
      if (!this.gerberaContent.contentId) {
        location = {
          name: 'editor-new-without-loading',
          params: Object.assign({}, this.$route.params, {
            kajiki_id: this.gerberaContent.siteId
          })
        }
      } else {
        location = {
          name: 'editor-update-without-loading',
          params: Object.assign({}, this.$route.params, {
            kajiki_id: this.gerberaContent.siteId,
            content_id: this.gerberaContent.contentId
          })
        }
      }
      this.$router.replace(location, resolve, () => {
        reject(new Error('URLの書き換えに失敗'))
      })
    })
  }

  async forceUpdateUrl(): Promise<void> {
    await this.updateUrl(true)
  }

  getHtmlContent(): string {
    return $(this.$el).find('.html-generator')[0].innerText.trim()
  }

  getScriptPool(): HTMLElement {
    return <HTMLElement>this.$refs.scriptPool
  }

  @Watch('embedScripts')
  refreshScriptPool(): void {
    // mounted() が呼ばれる前、記事データがロードされたときに embedScript の変更を検知して本メソッドが呼ばれる場合がある。
    // その場合、 pool が undefined になりエラーとなってしまうので、エラーを避ける。
    this.$nextTick().then(() => {
      setTimeout(() => {
        const pool = this.getScriptPool()
        if (pool) {
          loadScript(pool, this.embedScripts)
        }
      }, 100)
    })
  }

  undo(): void {
    Actions.requestSaveState(this.$store).then(() => {
      if (this.canUndo) {
        Actions.undo(this.$store)
      }
    })
  }

  redo(): void {
    Actions.requestSaveState(this.$store).then(() => {
      if (this.canRedo) {
        Actions.redo(this.$store)
      }
    })
  }

  @Watch('components', { deep: true })
  @Watch('currentTitle')
  onContentChanged(): void {
    if (this.isLoaded) {
      this.saveBackup()
    }
  }
}
